/**
 * ContractEditor - WYSIWYG editor for contract HTML
 * Features: inline editing, toolbar, save, version history, save as template
 */
import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Save, Undo2, Redo2, Bold, Italic, Underline, AlignLeft, AlignCenter,
  AlignJustify, History, BookTemplate, X, RotateCcw, Download, Printer,
  Type, List
} from "lucide-react";
import { safeOpenHTMLWindow } from "@/utils/safeHTML";
import DOMPurify from "dompurify";
import html2pdf from "html2pdf.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractEditor({ document, companyId, onClose, onSaved }) {
  const editorRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Initialize editor with contract HTML
  useEffect(() => {
    if (editorRef.current && document?.html_content) {
      // Extract just the body content from the full HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(document.html_content, "text/html");
      const container = doc.querySelector(".contract-container");
      if (container) {
        const footer = container.querySelector(".contract-footer");
        if (footer) footer.remove();
        editorRef.current.innerHTML = DOMPurify.sanitize(container.innerHTML, { ADD_TAGS: ["style"], ADD_ATTR: ["class", "style"] });
      } else {
        editorRef.current.innerHTML = DOMPurify.sanitize(doc.body.innerHTML, { ADD_TAGS: ["style"], ADD_ATTR: ["class", "style"] });
      }
    }
  }, [document]);

  const execCommand = (command, value = null) => {
    window.document.execCommand(command, false, value);
    editorRef.current?.focus();
    setHasChanges(true);
  };

  const getFullHTML = useCallback(() => {
    if (!editorRef.current) return "";
    const editorContent = editorRef.current.innerHTML;
    // Reconstruct full HTML document
    return `<!DOCTYPE html>
<html lang="ro">
<head>
  <meta charset="UTF-8">
  <title>${document?.title || "Contract"}</title>
  <style>
    @page { margin: 12mm; size: A4; }
    * { box-sizing: border-box; }
    body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; line-height: 1.6; color: #000; margin: 0; padding: 0; background: #fff; }
    .contract-container { width: 190mm; padding: 10mm; margin: 0 auto; border: 2px solid #000; background: #fff; }
    .header { text-align: center; margin-bottom: 14px; border-bottom: 2px solid #000; padding-bottom: 12px; }
    .header-logo { max-height: 50px; max-width: 150px; object-fit: contain; margin-bottom: 5px; }
    .header h1 { font-size: 14pt; margin: 3px 0; font-weight: bold; text-transform: uppercase; }
    .header .info { font-size: 9pt; line-height: 1.5; }
    .contract-title { text-align: center; font-weight: bold; font-size: 13pt; margin: 18px 0 6px; text-transform: uppercase; }
    .contract-number { text-align: center; font-weight: bold; font-size: 11pt; margin-bottom: 18px; }
    .article { margin: 0 0 10px 0; text-align: justify; page-break-inside: avoid; }
    .article-title { font-weight: bold; margin: 14px 0 8px; text-decoration: underline; }
    .indent { margin-left: 15px; }
    p { margin: 5px 0; line-height: 1.6; }
    .signatures { margin-top: 40px; page-break-inside: avoid; }
    .sig-row { display: flex; justify-content: space-between; }
    .sig-box { width: 45%; text-align: center; }
    .sig-line { margin-top: 50px; border-top: 1px solid #000; padding-top: 5px; }
    .contract-footer { text-align: center; font-size: 8pt; color: #666; margin-top: 30px; padding-top: 12px; border-top: 1px solid #ccc; }
    @media print { body { background: #fff !important; } .contract-container { border: 2px solid #000 !important; box-shadow: none !important; margin: 0 auto !important; } }
  </style>
</head>
<body>
<div class="contract-container">
${editorContent}
<div class="contract-footer">Document generat prin <a href="https://e-tesa.ro" target="_blank" style="color: #1d4ed8; text-decoration: underline; font-weight: 500;">e-Tesa</a></div>
</div>
</body>
</html>`;
  }, [document]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const html = getFullHTML();
      await axios.put(`${API}/contracts/${document.id}/html?company_id=${companyId}`, {
        html_content: html,
        saved_by: "admin",
      });
      setHasChanges(false);
      toast.success("Contract salvat cu succes!");
      if (onSaved) onSaved();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Eroare la salvarea contractului");
    } finally {
      setSaving(false);
    }
  };

  const fetchVersions = async () => {
    setLoadingVersions(true);
    try {
      const res = await axios.get(`${API}/contracts/${document.id}/versions?company_id=${companyId}`);
      setVersions(res.data.versions || []);
    } catch (error) {
      console.error("Error fetching versions:", error);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleShowVersions = () => {
    fetchVersions();
    setShowVersions(true);
  };

  const handleRestoreVersion = async (versionId) => {
    try {
      await axios.post(`${API}/contracts/${document.id}/restore/${versionId}?company_id=${companyId}`, {
        saved_by: "admin",
      });
      // Reload the document
      const res = await axios.get(`${API}/documents/${document.id}?company_id=${companyId}`);
      if (res.data?.html_content && editorRef.current) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(res.data.html_content, "text/html");
        const container = doc.querySelector(".contract-container");
        if (container) {
          const footer = container.querySelector(".contract-footer");
          if (footer) footer.remove();
          editorRef.current.innerHTML = DOMPurify.sanitize(container.innerHTML, { ADD_TAGS: ["style"], ADD_ATTR: ["class", "style"] });
        }
      }
      setShowVersions(false);
      setHasChanges(false);
      fetchVersions();
      toast.success("Versiune restaurată!");
    } catch (error) {
      console.error("Restore error:", error);
      toast.error("Eroare la restaurare");
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Introduceți un nume pentru template");
      return;
    }
    setSavingTemplate(true);
    try {
      const html = getFullHTML();
      await axios.post(`${API}/contract-templates?company_id=${companyId}`, {
        name: templateName,
        html_content: html,
      });
      toast.success("Template salvat!");
      setShowSaveTemplate(false);
      setTemplateName("");
    } catch (error) {
      console.error("Template save error:", error);
      toast.error("Eroare la salvare template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handlePrint = () => {
    const html = getFullHTML();
    safeOpenHTMLWindow(html);
  };

  const handleDownloadPDF = async () => {
    const html = getFullHTML();
    const container = window.document.createElement("div");
    container.innerHTML = DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true, ADD_TAGS: ["style"] });
    try {
      await html2pdf().set({
        margin: 0,
        filename: `${document.title || "Contract"}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4" },
      }).from(container).save();
      toast.success("PDF descărcat!");
    } catch (error) {
      console.error("PDF error:", error);
      toast.error("Eroare la generare PDF");
    }
  };

  const formatTime = (iso) => {
    try {
      return new Date(iso).toLocaleString("ro-RO", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch (error) {
      return iso;
    }
  };

  return (
    <div className="space-y-3" data-testid="contract-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-1 border-r border-slate-200 pr-2">
          <Button variant="ghost" size="sm" onClick={() => execCommand("bold")} title="Bold" data-testid="editor-bold">
            <Bold className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => execCommand("italic")} title="Italic">
            <Italic className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => execCommand("underline")} title="Subliniat">
            <Underline className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1 border-r border-slate-200 pr-2">
          <Button variant="ghost" size="sm" onClick={() => execCommand("justifyLeft")} title="Aliniere stânga">
            <AlignLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => execCommand("justifyCenter")} title="Centrare">
            <AlignCenter className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => execCommand("justifyFull")} title="Justificare">
            <AlignJustify className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1 border-r border-slate-200 pr-2">
          <Button variant="ghost" size="sm" onClick={() => execCommand("insertUnorderedList")} title="Listă">
            <List className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => execCommand("fontSize", "4")} title="Text mare">
            <Type className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1 border-r border-slate-200 pr-2">
          <Button variant="ghost" size="sm" onClick={() => execCommand("undo")} title="Undo">
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => execCommand("redo")} title="Redo">
            <Redo2 className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <Button variant="outline" size="sm" onClick={handleShowVersions} title="Istoric versiuni" data-testid="editor-versions-btn">
            <History className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Versiuni</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSaveTemplate(true)} title="Salvează ca template" data-testid="editor-save-template-btn">
            <BookTemplate className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Template</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} title="Printare">
            <Printer className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} title="Descarcă PDF">
            <Download className="w-4 h-4" />
          </Button>
          {/* Butonul Salvează: pe mobile duplicat în bara sticky bottom, ascuns aici < sm */}
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-teal-600 hover:bg-teal-700 text-white hidden sm:inline-flex"
            size="sm"
            data-testid="editor-save-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Se salvează..." : "Salvează"}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} title="Închide editorul" className="hidden sm:inline-flex">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Sticky bottom bar pe mobile portrait - Salvează + Închide vizibile mereu */}
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-3 py-2 flex items-center gap-2"
        style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          className="flex-shrink-0"
          data-testid="editor-close-mobile-btn"
        >
          <X className="w-4 h-4 mr-1" /> Închide
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-10"
          data-testid="editor-save-mobile-btn"
        >
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Se salvează…" : "Salvează contract"}
        </Button>
      </div>
      {/* Spacer pentru bara sticky pe mobile ca să nu acopere conținutul */}
      <div className="sm:hidden" style={{ height: "calc(3.5rem + env(safe-area-inset-bottom))" }} aria-hidden="true" />

      {hasChanges && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 flex items-center gap-1">
          Aveți modificări nesalvate
        </div>
      )}

      {/* WYSIWYG Editor Area */}
      <style>{`
        /* Mobile overrides for contract content inside editor */
        .contract-editor-wrap .contract-container {
          width: 100% !important;
          max-width: 190mm;
          margin: 0 auto !important;
          padding: 4mm !important;
          border: 1px solid #cbd5e1 !important;
          box-shadow: none !important;
        }
        .contract-editor-wrap .sig-row { flex-wrap: wrap; gap: 12px; }
        .contract-editor-wrap .sig-box { width: 100% !important; }
        .contract-editor-wrap table { max-width: 100% !important; }
        @media (min-width: 640px) {
          .contract-editor-wrap .contract-container { padding: 10mm !important; }
          .contract-editor-wrap .sig-box { width: 45% !important; }
        }
      `}</style>
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-lg overflow-x-auto overflow-y-visible contract-editor-wrap"
        style={{ minHeight: "600px" }}
      >
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={() => setHasChanges(true)}
          data-testid="contract-editor-content"
          className="outline-none p-2 sm:p-6"
          style={{
            fontFamily: "'Times New Roman', Times, serif",
            fontSize: "11pt",
            lineHeight: "1.6",
            minHeight: "600px",
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        />
      </div>

      {/* Versions Dialog */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-teal-600" />
              Istoric Versiuni
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {loadingVersions ? (
              <div className="text-center py-8 text-slate-500">Se încarcă...</div>
            ) : versions.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <History className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>Nicio versiune anterioară</p>
                <p className="text-xs mt-1">Versiunile se creează automat la fiecare salvare</p>
              </div>
            ) : (
              <div className="space-y-2">
                {versions.map((v, i) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        Versiunea {versions.length - i}
                      </p>
                      <p className="text-xs text-slate-500">{formatTime(v.saved_at)}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestoreVersion(v.id)}
                      data-testid={`restore-version-${v.id}`}
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Restaurează
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookTemplate className="w-5 h-5 text-teal-600" />
              Salvează ca Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nume Template</label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="ex: Contract standard prestări servicii"
                data-testid="template-name-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSaveTemplate(false)}>
                Anulează
              </Button>
              <Button
                onClick={handleSaveTemplate}
                disabled={savingTemplate}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="save-template-confirm-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                {savingTemplate ? "Se salvează..." : "Salvează Template"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
